import { useDeferredValue, useEffect, useMemo, useRef, useState } from 'react'
import type React from 'react'
import { useNavigate } from 'react-router-dom'
import { searchGuide } from '../utils/guideSearch'
import type { SearchResult } from '../utils/guideSearch'
import { useWorld } from '../contexts/WorldDataContext'

export function useGuideSearch() {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const [active, setActive] = useState(0)
  const inputRef = useRef<HTMLInputElement>(null)
  const navigate = useNavigate()
  const world = useWorld()
  const deferred = useDeferredValue(query)
  const groups = useMemo(() => searchGuide(deferred, world.docs), [deferred, world.docs])
  const results = useMemo(() => groups.flatMap((g) => g.results), [groups])
  const hasQuery = deferred.trim().length >= 2

  useEffect(() => setActive(0), [deferred])

  const go = (r: SearchResult) => {
    navigate(r.doc.path)
    setOpen(false)
    setQuery('')
    inputRef.current?.blur()
  }

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setOpen(true)
      setActive((a) => Math.min(a + 1, Math.max(results.length - 1, 0)))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, 0))
    } else if (e.key === 'Enter') {
      const r = results[active]
      if (r) {
        e.preventDefault()
        go(r)
      }
    } else if (e.key === 'Escape') {
      if (query) setQuery('')
      else {
        setOpen(false)
        inputRef.current?.blur()
      }
    }
  }

  const fill = (text: string) => {
    setQuery(text)
    setOpen(true)
    inputRef.current?.focus()
  }

  return { query, setQuery, open, setOpen, active, setActive, groups, results, hasQuery, go, onKeyDown, fill, inputRef, worldStatus: world.status }
}
